import { syncPdpIdentity } from '@pogi/pdp-refinements';
import { StandardEvents } from '@pogi/standard-events';
import {
  calculateShopifyUnitSavings,
  formatShopifyMoney,
  formatShopifyMoneyWithTemplate,
  formatShopifyUnitMoney,
  formatShopifyWholeMoney,
  resolveShopifyPdpPrice,
} from '@pogi/money';
import { PogiBuyBoxModel, readPogiBuyBoxContract } from '@pogi/buy-box-v2-core';
import {
  PogiBuyBoxHorizonAdapter,
  readPogiBuyBoxNativeState,
  revealPogiBuyBoxNativeControls,
} from '@pogi/buy-box-v2-horizon';
import { resolveConfiguredPlanSet } from '@pogi/buy-box-v2-subscription-config';

/*
 * pogi-buy-box-dispenser — isolated clone for the single-option Dispenser PDP
 * (Scent only, so the pills axis is disabled when it collides with the tile axis).
 * ----
 * pogi-buy-box — §5.1 shell, ported verbatim from BuyBox (pdp-components.jsx).
 * PILLS axis = grouped count ladder (• Tubs / • Travel packs, role label + check,
 * per-unit ¢ anchor + ~N estimate, SAVE badge, supply line on every pill).
 * TILE axis = scent (icon swatch + name + 2 descriptor lines) OR size (footprint
 * glyph). Drives Horizon's hidden native variant-picker + form (variant→media,
 * price, cart, selling plan stay native). Review count (Judge.me) + subscribe
 * discount (live selling plan) stay dynamic.
 */

/* i18n: strings come from the data blob's `i18n` object (Liquid renders them via
   `| t` from locales/*.json pogi.bb.*). EVERY read falls back to the exact English
   literal that used to be hardcoded, so a missing key can never change the EN site.
   [[var]] placeholders (not {{ }}) so Liquid's t-filter never eats them. */
const pogiTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};

/* k = stable role key (i18n lookup + supplyDesc logic); t = English fallback label */
const ROLE = {
  100: { k: 'quick', t: 'Quick refill' },
  120: { k: 'travel', t: 'Travel pack' },
  240: { k: 'popular', t: 'Most popular', accent: true },
  400: { k: 'value', t: 'Best value', accent: true },
};
// Fallback grouping by count when the pogi.pack_format metafield isn't set.
const FORMAT_BY_COUNT = { 100: 'tub', 400: 'tub', 120: 'packs', 240: 'packs' };
const ICONS = {
  sprout: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20v-8"/><path d="M12 12c0-3.3 2.7-6 6-6 0 3.3-2.7 6-6 6z"/><path d="M12 14c0-2.8-2.2-5-5-5 0 2.8 2.2 5 5 5z"/></svg>',
  droplet: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.6S5 9.2 5 14a7 7 0 0 0 14 0c0-4.8-7-11.4-7-11.4z"/></svg>',
};
// Stable size key from exact dimension tokens (EN inches; localized values use metric).
// Rendered size words are display-only and never participate in matching.
const SIZE_KEY = { '18x24': 'medium', '45x60': 'medium', '24x24': 'large', '60x60': 'large', '24x35': 'xl', '60x90': 'xl' };
const SIZE_FIT_EN = { medium: 'Small breeds & puppies', large: 'Most dogs', xl: 'Large breeds & seniors' };

/* POGI L10N FIX (2026-07-23): selling-plan names are LOCALIZED per storefront
   locale (9 locales: en/ja/fr/de/es/it + nl/pl/pt queued). The old parsers were
   EN(+JA)-only, so every EU plan name parsed null and interval matching fell
   back to wrong plans off-EN. Unit-word alternations + a no-digit=every-1 branch
   (fr «tous les mois», de „jeden Monat", es «cada mes», pl „co miesiąc», pt
   «mensal»). Match order month→week→day MUST hold: PL week words (tydzień/
   tygodni) contain the day stems (dzie/dni). Trip-tested against all live plan
   names ×6 locales + nl/pl/pt synthetics. */
const POGI_FREQ_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};
const pogiParseFreq = (raw) => {
  const s = String(raw).toLowerCase().replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xFEE0));
  // Pass 1: explicit "N <unit>" wins; with several numeric clauses the LAST one is
  // the frequency ("1 month supply – delivery every 2 weeks" / "Vorrat für einen
  // Monat – Lieferung alle 2 Wochen" must both parse week:2, never month:1).
  // matchAll (not match, which only sees the FIRST per-unit hit) so SAME-unit
  // compounds also keep the last clause ("2 weeks starter – every 4 weeks" = week:4).
  let best = null;
  for (const u of ['month', 'week', 'day']) {
    for (const m of s.matchAll(new RegExp('(\\d+)\\s*(?:' + POGI_FREQ_WORDS[u] + ')', 'g'))) {
      if (!best || m.index > best.idx) best = { u: u, n: parseInt(m[1], 10), idx: m.index };
    }
  }
  if (best) return { u: best.u, n: best.n };
  // Pass 2: a bare unit word = every-1 (fr «tous les mois», de „jeden Monat", pl „co miesiąc").
  for (const u of ['month', 'week', 'day']) {
    if (new RegExp(POGI_FREQ_WORDS[u]).test(s)) return { u: u, n: 1 };
  }
  return null;
};

// A11Y (audit P3, 2026-07-23): the buy box's radio-group containers, for the
// roving-tabindex pattern. .pogi-bb__purchase and [data-pogi-freq-opts] hold
// role="radio" buttons without a radiogroup role in the Liquid, so they are
// matched explicitly. The sizing modal's radios are deliberately NOT covered —
// it owns its own DOM lifecycle. Selectors absent from a given clone are
// harmless (querySelectorAll/closest simply never match).
const POGI_ROVE_GROUPS = '[data-pogi-formats], [data-pogi-sizes], [data-pogi-tiles], [data-pogi-counts], .pogi-bb__purchase, [data-pogi-freq-opts]';
// Eligible = focusable-for-roving: not disabled, not [hidden], and rendered.
// offsetParent is the visibility check: it is null when the radio OR any
// ancestor is display:none (the clones hide invalid pills via style.display
// and whole panels via [hidden]) — one check covers both, and none of these
// radios are position:fixed (the one case offsetParent cannot see). Chosen
// over getComputedStyle for cost (Codex countersign #19, 2026-07-23).
const POGI_ROVE_ELIGIBLE = (b) => !b.disabled && !b.hidden && b.offsetParent !== null;

class PogiBuyBoxDispenser extends HTMLElement {
  connectedCallback() {
    if (this.closest('.shopify-section')?.hasAttribute('data-pogi-buy-box-fallback')) {
      this.setAttribute('inert', '');
      this.setAttribute('aria-busy', 'false');
      delete this.dataset.pogiV2Owner;
      return;
    }
    const reconnectState = readPogiBuyBoxNativeState(this);
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Events = new AbortController();
    const dataEl = this.querySelector('[data-pogi-data]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.I = this.data.i18n || {};
    this.t = (k, fb, vars) => pogiTpl(this.I, k, fb, vars);

    this.tilePos = parseInt(this.dataset.tilePos, 10) || 0;
    this.pillsPos = parseInt(this.dataset.pillsPos, 10) || 1;
    this.formatPos = parseInt(this.dataset.formatPos, 10) || 0; // 3rd axis (Tub/Travel); 0 = none
    // Dispenser is single-option (Scent only): the Liquid pills fallback lands on
    // the same option as the tiles, i.e. no real count axis. Disable pills so the
    // scent tiles are the only selector and variant resolution stays consistent.
    if (this.pillsPos === this.tilePos) this.pillsPos = 0;
    this.tileKind = this.dataset.tileKind || 'scent';
    this.estPerDay = parseFloat(this.dataset.estPerDay) || 4;
    this.estUnit = this.dataset.estUnit || 'weeks';
    this.unitNoun = this.dataset.unitNoun || 'unit';
    const shipThreshold = parseInt(this.dataset.shipThreshold, 10);
    this.shipThreshold = Number.isFinite(shipThreshold) ? shipThreshold : 0;
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.currency = String(this.data.currency || 'USD').toUpperCase(); // presentment ISO for market-aware formatting
    this.section = this.closest('.shopify-section') || document;
    this.nativePicker = this.section.querySelector('variant-picker');
    this.nativeForm = document.getElementById(this.dataset.formId);

    this.v2Mode = ['observe', 'active'].includes(this.dataset.v2Mode) ? this.dataset.v2Mode : 'off';
    this.v2Contract = null;
    this.v2Model = null;
    this.v2Adapter = null;
    if (this.v2Mode !== 'off') {
      try {
        this.v2Contract = readPogiBuyBoxContract(this);
        this.v2Model = new PogiBuyBoxModel(this.v2Contract);
        if (this.v2Mode === 'active') {
          this.v2Adapter = new PogiBuyBoxHorizonAdapter(this, this.v2Model);
          this.v2Adapter.claim();
          this.dataset.pogiV2Owner = 'true';
        }
      } catch (error) {
        this.dataset.pogiV2Observe = 'invalid';
        console.error('[pogi-buy-box-v2] Dispenser contract/ownership initialization failed:', error);
        if (this.v2Mode === 'active') {
          this.#failV2(error);
          return;
        }
      }
    }

    try {
      const selectedId = reconnectState?.variantId || this.data.selectedId;
      const sel = this.data.variants.find((v) => String(v.id) === String(selectedId)) || this.data.variants[0];
      this.pill = sel && this.pillsPos ? sel.options[this.pillsPos - 1] : null;
      this.tile = this.tilePos ? sel.options[this.tilePos - 1] : null;
      this.intervalCount = parseInt(this.dataset.intervalCount, 10) || 3;
      this.subscribe = false;
      this.intervalPlans = [];
      this.planId = null;
      this.#applyVariantPlans();
      const selectedV2Variant = this.v2Model?.variantById(sel?.id);
      if (reconnectState && this.v2Mode === 'active') {
        this.v2Adapter.updateCurrentCartQuantityFromForm();
      }
      this.qty = this.v2Mode === 'active' && selectedV2Variant
        ? this.v2Adapter.quantityState(
          selectedV2Variant.id,
          reconnectState?.quantity ?? selectedV2Variant.quantityRule.min,
        ).value
        : reconnectState?.quantity ?? 1;

      this.pills = this.pillsPos ? this.#uniq(this.data.variants.map((v) => v.options[this.pillsPos - 1])) : [];
      this.tiles = this.tilePos ? this.#uniq(this.data.variants.map((v) => v.options[this.tilePos - 1])) : [];

      this.#renderPills();
      this.#renderTiles();
      this.#renderFrequencies();
      this.#ensureSellingPlanInput();
      this.#bind();
      this.section.addEventListener(StandardEvents.productSelect, (event) => {
        event.promise?.then(() => {
          this.update();
        }).catch((error) => {
          if (error?.name === 'AbortError') return;
          if (this.v2Mode === 'active') this.#failV2(error);
        });
      }, { signal: this.v2Events.signal });
      if (this.v2Mode === 'active') {
        document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
          event.promise?.then(({ detail }) => {
            if (!this.isConnected || !this.v2Adapter?.updateCartItems(detail?.items)) return;
            this.update();
          }).catch((error) => {
            if (error?.name !== 'AbortError') {
              console.warn('[pogi-buy-box-v2] Dispenser cart quantity refresh failed:', error);
            }
          });
        }, { signal: this.v2Events.signal });
        this.v2QuantityObserver = new MutationObserver((records) => {
          const changedInput = records.find((record) =>
            record.type === 'attributes'
            && record.attributeName === 'data-cart-quantity'
            && record.target instanceof HTMLInputElement)?.target;
          if (
            this.v2Mode !== 'active'
            || !(changedInput instanceof HTMLInputElement)
            || !this.v2Adapter?.updateCurrentCartQuantityFromForm(changedInput)
          ) return;
          this.update();
        });
        this.v2QuantityObserver.observe(this.v2Adapter.form, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['data-cart-quantity'],
        });
      }
      this.update();
      this.#injectGalleryBadge();
      this.#bindPressModal();
      this.querySelectorAll('[data-pogi-skeleton]').forEach((node) => node.remove());
      this.setAttribute('data-ready', '');
      this.removeAttribute('inert');
      this.setAttribute('aria-busy', 'false');
      this.dispatchEvent(new CustomEvent('pogi:buybox-ready', { bubbles: true }));
    } catch (error) {
      if (this.v2Mode === 'active') {
        console.error('[pogi-buy-box-v2] Dispenser active initialization failed:', error);
        this.#failV2(error);
        return;
      }
      throw error;
    }
  }

  disconnectedCallback() {
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Adapter?.release();
  }

  #failV2(error) {
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Mode = 'failed';
    if (this.v2Adapter) this.v2Adapter.revealNativeControls(error);
    else revealPogiBuyBoxNativeControls(this, error);
    delete this.dataset.pogiV2Owner;
  }

  // §13.4 — the mobile press card opens a native <dialog>. Backdrop click + the
  // close button both dismiss it. No-op when the card/dialog aren't rendered.
  #bindPressModal() {
    const openBtn = this.querySelector('[data-pogi-press-open]');
    const modal = this.querySelector('[data-pogi-press-modal]');
    if (!openBtn || !modal || typeof modal.showModal !== 'function') return;
    openBtn.addEventListener('click', () => modal.showModal());
    modal.querySelector('[data-pogi-press-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
  }

  // §26.1 — pin a BESTSELLER chip top-left over the hero gallery tile. The gallery
  // lives in the same product-information section as the buy box, so we reach it
  // from here. Idempotent (guards against the product-select re-render).
  #injectGalleryBadge() {
    const text = (this.dataset.galleryBadge || '').trim();
    if (!text) return;
    const tile = this.section.querySelector('.product-media-container');
    if (!tile || tile.querySelector('.pogi-gallery-badge')) return;
    const chip = document.createElement('span');
    chip.className = 'pogi-gallery-badge';
    chip.textContent = text;
    tile.appendChild(chip);
  }

  #uniq(a) { return a.filter((x, i) => x != null && a.indexOf(x) === i); }
  #esc(s) { return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }

  money(cents) {
    // Match the Liquid `money` output beside JS-rendered prices and totals.
    if (this.data?.moneyFormat) return formatShopifyMoneyWithTemplate(cents, this.data.moneyFormat, this.currency || 'USD');
    return formatShopifyMoney(cents, this.moneyFormat, this.currency || 'USD');
  }
  moneyWhole(cents) {
    return formatShopifyWholeMoney(cents, this.moneyFormat, this.currency || 'USD');
  }
  unitMoney(cents, count) {
    return formatShopifyUnitMoney(cents, count, this.currency || 'USD');
  }
  showDerived() { return this.currency === 'USD'; }
  priceMoney(v) {
    return (!this.showDerived() && v?.money) ? v.money : this.money(v ? v.price : 0);
  }
  cardPrice(v, plan) {
    return resolveShopifyPdpPrice(v, this.subscribe ? this.#allocationFor(v, plan) : null, this.subscribe);
  }
  cardMoney(v, price) {
    if (!price) return '';
    return !this.showDerived() && price.money
      ? price.money
      : this.money(price.amount);
  }
  #allocationFor(variant, plan) {
    if (!variant || !plan || !Array.isArray(variant.allocations)) return null;
    if (this.v2Mode === 'active' && this.v2Model && !this.v2Model.allocationFor(variant.id, plan.id)) {
      return null;
    }
    return variant.allocations.find((allocation) =>
      String(allocation.planId) === String(plan.id)
      && Number.isSafeInteger(allocation.price)
      && allocation.price >= 0
      && Number.isSafeInteger(allocation.savings)
    ) || null;
  }
  parseCount(v) {
    if (!v) return 0;
    const m = String(v).match(/(\d[\d,]*)\s*-?\s*count/i);
    if (m) return parseInt(m[1].replace(/,/g, ''), 10);
    const n = String(v).match(/\d[\d,]*/);
    return n ? parseInt(n[0].replace(/,/g, ''), 10) : 0;
  }
  variantObj(pillVal) {
    // 3-axis: a pill (Qty) implies its Format, so resolve on Format + Qty + Scent.
    const fmt = this.formatPos > 0 ? this.formatOf(pillVal) : null;
    const legacyVariant = this.data.variants.find((v) =>
      (this.pillsPos === 0 || v.options[this.pillsPos - 1] === pillVal)
      && (this.tilePos === 0 || v.options[this.tilePos - 1] === this.tile)
      && (this.formatPos === 0 || v.options[this.formatPos - 1] === fmt));
    return this.#v2CommerceVariant(legacyVariant);
  }

  #v2CommerceVariant(legacyVariant) {
    if (this.v2Mode !== 'active' || !this.v2Model || !legacyVariant) return legacyVariant || null;
    const normalized = this.v2Model.variantById(legacyVariant.id);
    if (!normalized) return null;
    return {
      ...legacyVariant,
      id: normalized.id,
      available: normalized.available,
      price: normalized.price.shopifyAmount,
      money: normalized.price.formatted,
      options: [...this.v2Contract.axes]
        .sort((left, right) => left.optionPosition - right.optionPosition)
        .map((axis) => normalized.selections[axis.key]),
      allocations: normalized.allocations.map((allocation) => ({
        planId: allocation.planId,
        price: allocation.price.shopifyAmount,
        money: allocation.price.formatted,
        savings: allocation.savings.shopifyAmount,
        savingsMoney: allocation.savings.formatted,
      })),
      mediaId: normalized.mediaId,
      sub: normalized.subscriptionConfig,
    };
  }
  // Format of a pill. 3-axis (Format option present) → the REAL Format value
  // (Tub / Travel Packs). 2-axis fallback → legacy metafield/count heuristic.
  formatOf(pillVal) {
    const v = this.data.variants.find((x) => x.options[this.pillsPos - 1] === pillVal);
    if (this.formatPos > 0) return v ? v.options[this.formatPos - 1] : null;
    if (v && v.packFormat) return v.packFormat === 'packs' ? 'packs' : 'tub';
    return FORMAT_BY_COUNT[this.parseCount(pillVal)] || 'tub';
  }
  dims(v) { const s = String(v).replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0)); const m = s.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/); return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null; }
  estN(count) { return this.estUnit === 'days' ? Math.max(1, Math.round(count / this.estPerDay)) : Math.max(1, Math.round(count / this.estPerDay / 7)); }
  unitWord(n) {
    const plural = n !== undefined && n !== 1;
    return this.estUnit === 'days'
      ? this.t(plural ? 'js_unit_days' : 'js_unit_day', plural ? 'days' : 'day')
      : this.t(plural ? 'js_unit_weeks' : 'js_unit_week', plural ? 'weeks' : 'week');
  }
  unitAbbr() { return this.estUnit === 'days' ? this.t('js_abbr_day', 'd') : this.t('js_abbr_week', 'wk'); }
  supplyDesc(roleK) {
    if (roleK === 'quick') return this.t('js_supply_quick', 'Lowest commitment');
    if (roleK === 'value') return this.t('js_supply_value', 'Lowest cost per [[unit]]', { unit: this.unitNoun });
    return this.t('js_supply_stock', 'Stock-up size');
  }
  refPerUnit(plan) {
    let reference = null;
    this.pills.forEach((s) => {
      const v = this.variantObj(s);
      const count = this.parseCount(s);
      const price = this.cardPrice(v, plan);
      if (price && count && (!reference || (price.amount / count) > (reference.amount / reference.count))) {
        reference = { amount: price.amount, count };
      }
    });
    return reference;
  }
  #v2Selections() {
    const selections = {};
    for (const axis of this.v2Contract?.axes || []) {
      if (axis.key === 'format') selections[axis.key] = this.formatOf(this.pill);
      else if (axis.key === 'count') selections[axis.key] = this.pill;
      else if (axis.key === this.tileKind) selections[axis.key] = this.tile;
    }
    return selections;
  }
  currentVariant() {
    if (this.v2Mode === 'active' && this.v2Model) {
      const normalized = this.v2Model.resolveVariant(this.#v2Selections());
      const legacyVariant = normalized
        ? this.data.variants.find((variant) => String(variant.id) === normalized.id)
        : null;
      return this.#v2CommerceVariant(legacyVariant);
    }
    return this.variantObj(this.pill);
  }

  #recordV2Observation() {
    if (this.v2Mode !== 'observe' || !this.v2Model) return;
    const legacyVariant = this.variantObj(this.pill);
    const normalizedVariant = this.v2Model.resolveVariant(this.#v2Selections());
    const mismatches = [];
    if (String(legacyVariant?.id ?? '') !== String(normalizedVariant?.id ?? '')) mismatches.push('variant');
    if (legacyVariant && normalizedVariant) {
      if (Boolean(legacyVariant.available) !== normalizedVariant.available) mismatches.push('availability');
      if (Number(legacyVariant.price) !== normalizedVariant.price.shopifyAmount) mismatches.push('price');
      const legacyAllocations = (legacyVariant.allocations || [])
        .map((allocation) => `${allocation.planId}:${allocation.price}:${allocation.savings}`)
        .sort();
      const normalizedAllocations = normalizedVariant.allocations
        .map((allocation) => `${allocation.planId}:${allocation.price.shopifyAmount}:${allocation.savings.shopifyAmount}`)
        .sort();
      if (legacyAllocations.join('|') !== normalizedAllocations.join('|')) mismatches.push('allocations');

      const configuredPlans = resolveConfiguredPlanSet(this.v2Model, normalizedVariant.id).plans
        .map((plan) => plan.id)
        .sort();
      const legacyPlans = (this.intervalPlans || []).map((plan) => String(plan.id)).sort();
      if (configuredPlans.join('|') !== legacyPlans.join('|')) mismatches.push('plans');

      const quantityInput = this.nativeForm?.querySelector('input[name="quantity"]');
      const quantityOwner = quantityInput?.closest('quantity-selector-component');
      if (
        quantityInput instanceof HTMLInputElement
        && quantityOwner instanceof HTMLElement
        && quantityOwner.dataset.variantId === normalizedVariant.id
      ) {
        const rule = normalizedVariant.quantityRule;
        const nativeRule = {
          min: Number(quantityInput.min || 1),
          max: quantityInput.max ? Number(quantityInput.max) : null,
          step: Number(quantityInput.step || 1),
          cartQuantity: Number(quantityInput.dataset.cartQuantity || 0),
        };
        if (JSON.stringify(rule) !== JSON.stringify(nativeRule)) mismatches.push('quantity');
      }
    }
    this.dataset.pogiV2Observe = mismatches.length ? 'mismatch' : 'match';
    if (mismatches.length) {
      console.warn('[pogi-buy-box-v2] Dispenser observation mismatch:', {
        mismatches,
        legacyVariantId: legacyVariant?.id ?? null,
        normalizedVariantId: normalizedVariant?.id ?? null,
      });
    }
  }

  // Grouped pills by Format. 3-axis → real Format values (Tub / Travel Packs);
  // 2-axis → legacy 'tub'/'packs' heuristic. Headers show only with >1 group.
  #renderPills() {
    const wrap = this.querySelector('[data-pogi-sizes]');
    if (!wrap) return;
    const groups = this.#uniq(this.pills.map((s) => this.formatOf(s)));
    const showHeaders = groups.length > 1;
    const labelOf = (g) => (g === 'tub' ? this.t('js_group_tub', 'Tubs') : g === 'packs' ? this.t('js_group_packs', 'Travel packs') : g);
    const modOf = (g, i) => (g === 'tub' ? 'tubs' : g === 'packs' ? 'packs' : (i === 0 ? 'tubs' : 'packs'));
    let html = '';
    groups.forEach((g, i) => {
      const items = this.pills.filter((s) => this.formatOf(s) === g);
      if (!items.length) return;
      html += `<div class="pogi-bb__group">${showHeaders ? `<div class="pogi-bb__group-header pogi-bb__group-header--${modOf(g, i)}"><span class="pogi-bb__group-dot"></span>${this.#esc(labelOf(g))}</div>` : ''}<div class="pogi-bb__pills">${items.map((s) => this.#pill(s)).join('')}</div></div>`;
    });
    wrap.innerHTML = html;
  }
  #pill(v) {
    const role = ROLE[this.parseCount(v)] || { t: '' };
    return `<button type="button" class="pogi-bb__pill" role="radio" data-pogi-size="${this.#esc(v)}" aria-checked="false">
      <span class="pogi-bb__pill-top"><span class="pogi-bb__pill-role${role.accent ? ' pogi-bb__pill-role--accent' : ''}">${role.k ? this.t('js_role_' + role.k, role.t) : ''}</span><span class="pogi-bb__pill-check" aria-hidden="true">✓</span></span>
      <span class="pogi-bb__pill-mid"><span class="pogi-bb__pill-size">${this.#esc(v)}</span><span class="pogi-bb__pill-price" data-pill-price></span></span>
      <span class="pogi-bb__pill-bot"><span class="pogi-bb__pill-per" data-pill-per></span><span class="pogi-bb__pill-save" data-pill-save></span></span>
      <span class="pogi-bb__pill-supply" data-pill-supply></span>
    </button>`;
  }

  #renderTiles() {
    const wrap = this.querySelector('[data-pogi-tiles]');
    if (!wrap || !this.tiles.length) return;
    if (this.tileKind === 'size') {
      let maxDim = 1;
      this.tiles.forEach((t) => { const d = this.dims(t); if (d) maxDim = Math.max(maxDim, d.w, d.h); });
      const scale = 40 / maxDim;
      wrap.innerHTML = this.tiles.map((t) => {
        const d = this.dims(t) || { w: 24, h: 24 };
        const name = String(t).split(/[（(]/)[0].trim();
        const dimStr = this.t('js_dims', '[[w]] × [[h]] in', { w: d.w, h: d.h });
        const sk = SIZE_KEY[`${d.w}x${d.h}`] || '';
        const fit = sk ? this.t('js_fit_' + sk, SIZE_FIT_EN[sk]) : '';
        return `<button type="button" class="pogi-bb__sizetile" role="radio" data-pogi-tile="${this.#esc(t)}" aria-checked="false">
          <span class="pogi-bb__sizetile-glyphwrap"><span class="pogi-bb__sizetile-glyph" style="width:${(d.w * scale).toFixed(0)}px;height:${(d.h * scale).toFixed(0)}px"></span></span>
          <span class="pogi-bb__sizetile-name">${this.#esc(name)}</span>
          <span class="pogi-bb__sizetile-dims">${dimStr}</span>
          ${fit ? `<span class="pogi-bb__sizetile-fit">${fit}</span>` : ''}
        </button>`;
      }).join('');
    } else {
      wrap.innerHTML = this.tiles.map((s) => {
        const ff = /^\s*(?:fragrance-free|unscented|無香料?|sans parfum|ohne (?:parf(?:ü|ü)m|duftstoffe)|sin perfume|senza profumo|zonder (?:parfum|geur)|bezzapach[a-ząćęłńóśźż]*|sem perfume)\s*$/i.test(s); // exact-value ff detection, all 9 locales (NFC+NFD de); full phrases only — generic scent words (parfum/duft/geur/perfume) must NOT match
        const icon = ff ? ICONS.droplet : ICONS.sprout;
        const accent = ff ? 'var(--pogi-olive-deep)' : 'var(--pogi-olive)';
        return `<button type="button" class="pogi-bb__scent" role="radio" data-pogi-tile="${this.#esc(s)}" aria-checked="false">
          <span class="pogi-bb__scent-icon" style="background:${accent}">${icon}</span>
          <span class="pogi-bb__scent-text"><span class="pogi-bb__scent-name">${this.#esc(s)}</span></span>
        </button>`;
      }).join('');
    }
  }

  // Convert a selling-plan interval to weeks (per BuyBox — intervals shown in weeks).
  #freqWeeks(name) {
    const pi = pogiParseFreq(name);
    if (!pi) return null;
    if (pi.u === 'month') return Math.round((pi.n * 30) / 7);
    if (pi.u === 'week') return pi.n;
    return Math.round(pi.n / 7);
  }
  #parseInterval(name) {
    return pogiParseFreq(name); // shared locale-proof parser (see POGI_FREQ_WORDS above)
  }
  // Per-variant interval config injected as variant.sub ("unit|opts|default").
  #cfgFor(variant) {
    const raw = variant && variant.sub;
    if (!raw) return null;
    const parts = String(raw).split('|');
    if (parts.length < 2 || !parts[1]) return null;
    const options = [...new Set(parts[1].split(',').map(Number).filter(Number.isFinite))];
    return { u: parts[0], o: options, d: Number(parts[2]) };
  }
  // Recharge plans are product-level; show only the selected variant's configured
  // intervals (matched to live plans by parsed interval), default to its mid option.
  #applyVariantPlans() {
    const all = (this.data.sellingPlan && this.data.sellingPlan.plans) || [];
    const variant = this.currentVariant();
    if (this.v2Mode === 'active' && this.v2Model && variant) {
      const resolved = resolveConfiguredPlanSet(this.v2Model, variant.id, this.planId);
      const plans = resolved.plans
        .map((plan) => all.find((legacyPlan) => String(legacyPlan.id) === plan.id))
        .filter(Boolean);
      this.intervalPlans = plans;
      const sig = plans.map((plan) => plan.id).join(',');
      const selected = plans.find((plan) => String(plan.id) === resolved.selectedPlanId) || null;
      if (sig !== this._planSig) {
        this._planSig = sig;
        this.planId = selected?.id ?? plans[0]?.id ?? null;
        this.#renderFrequencies();
      } else if (!plans.some((plan) => String(plan.id) === String(this.planId))) {
        this.planId = selected?.id ?? plans[0]?.id ?? null;
      }
      return;
    }
    const cfg = this.#cfgFor(variant);
    let plans;
    if (cfg) {
      plans = cfg.o
        .map((c) => all.find((p) => {
          const pi = this.#parseInterval(p.name);
          return pi && pi.u === cfg.u && pi.n === c && this.#allocationFor(variant, p);
        }))
        .filter(Boolean);
    } else {
      // The dispenser currently has no plans. If plans are introduced later,
      // require an explicit per-variant cadence contract before enabling them.
      plans = [];
    }
    this.intervalPlans = plans;
    const sig = plans.map((p) => p.id).join(',');
    if (sig !== this._planSig) {
      this._planSig = sig;
      const def = cfg ? plans.find((p) => { const pi = this.#parseInterval(p.name); return pi && pi.n === cfg.d; }) : null;
      this.planId = (def && def.id) || (plans[0] && plans[0].id) || null;
      this.#renderFrequencies();
    } else if (!plans.some((p) => p.id === this.planId)) {
      this.planId = (plans[0] && plans[0].id) || null;
    }
  }
  #renderFrequencies() {
    const wrap = this.querySelector('[data-pogi-freq-opts]');
    if (!wrap) return;
    if (!this.intervalPlans || !this.intervalPlans.length) {
      wrap.innerHTML = '';
      return;
    }
    const intervalUnit = (u, n) => {
      const plural = n > 1;
      if (u === 'month') return this.t(plural ? 'js_unit_months' : 'js_unit_month', plural ? 'months' : 'month');
      if (u === 'day') return this.t(plural ? 'js_unit_days' : 'js_unit_day', plural ? 'days' : 'day');
      return this.t(plural ? 'js_unit_weeks' : 'js_unit_week', plural ? 'weeks' : 'week');
    };
    wrap.innerHTML = this.intervalPlans.map((p) => {
      const pi = this.#parseInterval(p.name);
      const label = pi ? this.t('js_freq', '[[dur]]', { dur: this.t('js_dur', '[[n]] [[unit]]', { n: pi.n, unit: intervalUnit(pi.u, pi.n) }) }) : String(p.name);
      return `<button type="button" class="pogi-bb__freq-opt" role="radio" data-pogi-freq="${p.id}" aria-checked="${p.id === this.planId}">${label}</button>`;
    }).join('');
  }

  #bind() {
    this.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-pogi-size]');
      const tile = e.target.closest('[data-pogi-tile]');
      const mode = e.target.closest('[data-pogi-mode]');
      const freq = e.target.closest('[data-pogi-freq]');
      const dt = e.target.closest('[data-pogi-desc-toggle]');
      const dec = e.target.closest('[data-pogi-qty-dec]');
      const inc = e.target.closest('[data-pogi-qty-inc]');
      if (pill) { this.pill = pill.dataset.pogiSize; this.#commitSelection(); }
      else if (tile) { this.tile = tile.dataset.pogiTile; this.#commitSelection(); }
      else if (mode) { this.subscribe = mode.dataset.pogiMode === 'subscribe'; this.update(); }
      else if (freq) { this.planId = parseInt(freq.dataset.pogiFreq, 10); this.update(); }
      else if (dt) { const d = this.querySelector('[data-pogi-desc]'); const o = d.classList.toggle('is-open'); dt.textContent = o ? this.t('read_less', 'Read less') : this.t('read_more', 'Read more'); dt.setAttribute('aria-expanded', String(o)); }
      else if (dec) { this.#changeQty(-1); }
      else if (inc) { this.#changeQty(1); }
    }, { signal: this.v2Events.signal });
    // A11Y (audit P3, 2026-07-23): ONE delegated keydown per component root —
    // roving-tabindex arrow-key support for the radio groups (see #roveKey).
    this.addEventListener('keydown', (e) => this.#roveKey(e), { signal: this.v2Events.signal });
  }

  #commitSelection() {
    try {
      this.#driveNative();
      this.update();
    } catch (error) {
      if (this.v2Mode !== 'active') throw error;
      console.error('[pogi-buy-box-v2] Dispenser selection failed closed:', error);
      this.#failV2(error);
    }
  }

  /* ---- a11y: roving tabindex (audit P3 + countersign #19, 2026-07-23) ---- */
  // ONE tab stop per radio group: tabindex 0 on the checked ELIGIBLE radio
  // (first eligible if none), -1 on every other radio — hidden/disabled ones
  // included, so they can never hold a stale tab stop. Called at the END of
  // update(), AFTER the pill display-sync and [hidden] toggles, so eligibility
  // is judged on the pass's final visibility state. Behavior-additive: click
  // handling is untouched.
  #rove() {
    this.querySelectorAll(POGI_ROVE_GROUPS).forEach((g) => {
      const all = [...g.querySelectorAll('[role="radio"]')];
      const radios = all.filter(POGI_ROVE_ELIGIBLE);
      all.forEach((b) => b.setAttribute('tabindex', '-1')); // hidden/disabled are never the tab stop
      if (!radios.length) return;
      const on = radios.find((b) => b.getAttribute('aria-checked') === 'true') || radios[0];
      on.setAttribute('tabindex', '0'); // checked-and-eligible, else first ELIGIBLE
    });
  }
  // Arrow keys move focus AND activate the next/prev radio with wrap-around;
  // Home/End jump to first/last. Activation goes through .click() — the exact
  // path pointer input takes — so selection, native-picker drive and update()
  // all reuse the existing delegated click handler above.
  #roveKey(e) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    const radio = e.target.closest('[role="radio"]');
    const group = radio && radio.closest(POGI_ROVE_GROUPS);
    if (!group) return; // not one of our groups (e.g. the sizing modal) — leave the key alone
    const radios = [...group.querySelectorAll('[role="radio"]')].filter(POGI_ROVE_ELIGIBLE); // arrows skip hidden/disabled radios
    const i = radios.indexOf(radio);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'Home' ? radios[0]
      : e.key === 'End' ? radios[radios.length - 1]
        : (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? radios[(i + 1) % radios.length]
          : radios[(i - 1 + radios.length) % radios.length];
    if (next === radio) return;
    next.focus();
    next.click();
    // A click can rebuild the clicked group's innerHTML (e.g. compostable freqs
    // re-render on selection) — re-focus the fresh tab stop so keyboard context
    // is never dropped mid-interaction.
    if (!next.isConnected) { const nb = group.querySelector('[role="radio"][tabindex="0"]'); if (nb) nb.focus(); }
  }
  #setNativeOption(fieldsetIndex, value) {
    if (!this.nativePicker || fieldsetIndex < 0) return;
    const inputs = this.nativePicker.querySelectorAll(`input[data-fieldset-index="${fieldsetIndex}"]`);
    for (const input of inputs) {
      if (input.value === value && !input.checked) { input.checked = true; input.dispatchEvent(new Event('input', { bubbles: true })); input.dispatchEvent(new Event('change', { bubbles: true })); return; }
    }
  }
  #driveNative() {
    if (this.v2Mode === 'active' && this.v2Adapter && this.v2Model) {
      const variant = this.currentVariant();
      if (!variant) throw new Error('The selected Dispenser options do not resolve to one variant');
      this.v2Adapter.selectVariant(variant.id);
      return;
    }
    // Drive the Format option first (a pill implies its format) so the native
    // picker resolves to the correct Tub/Travel variant — the whole point of this
    // clone. Then scent + qty.
    if (this.formatPos > 0 && this.pill != null) {
      const fmt = this.formatOf(this.pill);
      if (fmt != null) this.#setNativeOption(this.formatPos - 1, fmt);
    }
    if (this.tile != null) this.#setNativeOption(this.tilePos - 1, this.tile);
    if (this.pill != null) this.#setNativeOption(this.pillsPos - 1, this.pill);
  }

  plan() {
    return (this.intervalPlans || []).find((p) => p.id === this.planId) || null;
  }

  update() {
    this.#applyVariantPlans();
    const variant = this.currentVariant();
    if (this.v2Mode === 'active' && !variant) {
      this.#failV2(new Error('The selected Dispenser options do not resolve to one variant'));
      return;
    }
    const selectedPlan = this.plan();
    const allocation = this.#allocationFor(variant, selectedPlan);
    const subscriptionValid = Boolean(selectedPlan && allocation);
    const ref = this.refPerUnit(selectedPlan);
    const quantityState = this.v2Mode === 'active' && this.v2Model && variant
      ? this.v2Adapter.quantityState(variant.id, this.qty)
      : null;
    if (quantityState) {
      this.qty = quantityState.value;
      const quantityOutput = this.querySelector('[data-pogi-qty]');
      if (quantityOutput) quantityOutput.textContent = this.qty;
      const decrement = this.querySelector('[data-pogi-qty-dec]');
      const increment = this.querySelector('[data-pogi-qty-inc]');
      const activeStepper = [decrement, increment].find((button) => button && button === document.activeElement);
      if (decrement) decrement.disabled = this.qty <= quantityState.min;
      if (increment) {
        increment.disabled = !quantityState.canAdd
          || (quantityState.remaining !== null && this.qty + quantityState.step > quantityState.remaining);
      }
      // A focused stepper that disables itself would drop keyboard focus to the
      // page; move focus to the other stepper while that one is still usable.
      if (activeStepper?.disabled) [decrement, increment].find((button) => button && button !== activeStepper && !button.disabled)?.focus();
    }

    this.querySelectorAll('[data-pogi-size]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiSize === this.pill)));
    this.querySelectorAll('[data-pogi-tile]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiTile === this.tile)));
    this.querySelectorAll('[data-pogi-mode]').forEach((b) => b.setAttribute('aria-checked', String((b.dataset.pogiMode === 'subscribe') === this.subscribe)));
    const subscribeMode = this.querySelector('[data-pogi-mode="subscribe"]');
    if (subscribeMode) {
      subscribeMode.disabled = !subscriptionValid;
      subscribeMode.setAttribute('aria-disabled', String(!subscriptionValid));
    }
    this.querySelectorAll('[data-pogi-freq]').forEach((b) => b.setAttribute('aria-checked', String(parseInt(b.dataset.pogiFreq, 10) === this.planId)));
    const tlab = this.querySelector('[data-pogi-tile-label]');
    if (tlab && this.tile) tlab.textContent = this.tileKind === 'size' ? String(this.tile).split(/[（(]/)[0].trim() : this.tile;

    this.querySelectorAll('[data-pogi-size]').forEach((btn) => {
      const pv = btn.dataset.pogiSize;
      const v = this.variantObj(pv);
      const count = this.parseCount(pv);
      const role = ROLE[count] || { t: '' };
      const setT = (sel, val) => { const el = btn.querySelector(sel); if (el) el.textContent = val; };
      const price = this.cardPrice(v, selectedPlan);
      if (v && price) {
        const n = this.estN(count);
        setT('[data-pill-price]', this.cardMoney(v, price));
        const perLine = !count ? '' : this.showDerived()
          ? this.t('js_per_unit', '[[cents]]¢/[[unit]] · ~[[n]] [[abbr]]', { cents: Math.round(price.amount / count), unit: this.unitNoun, n, abbr: this.unitAbbr() })
          : this.t('js_per_unit_money', '[[money]]/[[unit]] · ~[[n]] [[abbr]]', { money: this.unitMoney(price.amount, count), unit: this.unitNoun, n, abbr: this.unitAbbr() });
        setT('[data-pill-per]', perLine);
        const saveCents = ref ? calculateShopifyUnitSavings(ref.amount, ref.count, price.amount, count) : 0;
        setT('[data-pill-save]', this.showDerived() && saveCents > 0 ? this.t('js_save_caps', 'SAVE [[amount]]', { amount: this.moneyWhole(saveCents) }) : '');
        setT('[data-pill-supply]', count > 0 ? this.t('js_supply_line', '[[desc]] · [[n]]-[[unit]] supply', { desc: this.supplyDesc(role.k), n, unit: this.unitWord(1) }) : '');
        btn.style.display = '';
      } else if (v && pv === this.pill) {
        setT('[data-pill-price]', '');
        setT('[data-pill-per]', '');
        setT('[data-pill-save]', '');
        setT('[data-pill-supply]', '');
        btn.style.display = '';
      } else { btn.style.display = 'none'; }
    });

    const est = this.querySelector('[data-pogi-estimate]');
    const count = this.parseCount(this.pill);
    if (est && count > 0) {
      const n = this.estN(count);
      est.querySelector('[data-pogi-est-pack]').textContent = this.pill;
      est.querySelector('[data-pogi-est-weeks]').textContent = this.t('js_dur', '[[n]] [[unit]]', { n, unit: this.unitWord(n) });
      est.hidden = false;
    } else if (est) { est.hidden = true; }

    if (variant) {
      const savings = subscriptionValid ? Math.max(0, allocation.savings) : 0;
      const setT = (sel, val) => { const el = this.querySelector(sel); if (el) el.textContent = val; };
      setT('[data-pogi-sub-price]', subscriptionValid
        ? (this.showDerived() ? this.money(allocation.price) : (allocation.money || this.priceMoney(variant)))
        : this.priceMoney(variant));
      setT('[data-pogi-sub-was]', subscriptionValid && savings > 0
        ? (this.showDerived() ? this.money(variant.price) : this.priceMoney(variant))
        : '');
      setT('[data-pogi-ot-price]', this.priceMoney(variant));
      const badge = this.querySelector('[data-pogi-save-badge]');
      if (badge) badge.textContent = subscriptionValid && savings > 0
        ? (
            this.showDerived()
              ? this.t('js_save', 'Save [[amount]]', { amount: this.money(savings) })
              : this.t('js_save_pct', '[[pct]]% off', {
                  pct: Math.round((savings / variant.price) * 100),
                })
          )
        : '';
    }

    const subx = this.querySelector('[data-pogi-subx]');
    if (subx) subx.hidden = !this.subscribe || !subscriptionValid;
    const subxDesc = this.querySelector('[data-pogi-subx-desc]');
    if (subxDesc && count > 0) {
      const n = this.estN(count);
      const dur = this.t('js_dur', '[[n]] [[unit]]', { n, unit: this.unitWord(n) });
      subxDesc.textContent = this.t('js_subx_desc', '[[pack]] lasts about [[dur]]. We send your next pack just before you run low.', { pack: this.pill, dur });
    }

    if (this.v2Mode === 'active' && this.v2Adapter && variant) {
      const state = this.v2Adapter.syncPurchaseInputs({
        variantId: variant.id,
        sellingPlanId: this.subscribe && subscriptionValid ? selectedPlan.id : null,
        quantity: this.qty,
      });
      this.qty = state.value;
    } else {
      this.#ensureSellingPlanInput();
      const planInput = this.nativeForm && this.nativeForm.querySelector('input[name="selling_plan"]');
      if (planInput) planInput.value = this.subscribe && selectedPlan ? selectedPlan.id : '';
      // Write the variant id synchronously too (mirrors Horizon's async picker write),
      // closing the race where a fast option-change → ATC could submit a stale id.
      const idInput = this.nativeForm && this.nativeForm.querySelector('input[name="id"]');
      if (idInput && variant) { idInput.value = variant.id; idInput.dataset.pogiSync = '1'; } // POGI: marks this form synchronously managed (product-form #getIntendedVariantId)
    }
    const atc = this.querySelector('[data-pogi-atc]');
    const atcLabel = this.querySelector('[data-pogi-atc-label]');
    let identityPrice = '';
    if (!variant && atc) {
      atc.disabled = true;
      if (atcLabel) atcLabel.textContent = this.t('js_sold_out', 'Unavailable');
    } else if (variant && atc) {
      const invalidSubscription = this.subscribe && !subscriptionValid;
      const invalidQuantity = quantityState ? variant.available && !quantityState.canAdd : false;
      atc.disabled = !variant.available || invalidSubscription || invalidQuantity;
      const priceStr = this.qty > 1
        ? this.money((this.subscribe && subscriptionValid ? allocation.price : variant.price) * this.qty)
        : this.showDerived()
          ? this.money(this.subscribe && subscriptionValid ? allocation.price : variant.price)
          : (
              this.subscribe && subscriptionValid
                ? (allocation.money || this.priceMoney(variant))
                : this.priceMoney(variant)
            );
      identityPrice = priceStr;
      atcLabel.textContent = invalidSubscription || invalidQuantity
        ? this.t('js_sold_out', 'Unavailable')
        : variant.available
          ? (this.subscribe ? this.t('js_atc_sub', 'Start my routine · ') : this.t('js_atc_add', 'Add to cart · ')) + priceStr
          : this.t('js_sold_out', 'Sold out');
    }

    syncPdpIdentity(this, { price: identityPrice, available: Boolean(atc && !atc.disabled),
      subscribed: this.subscribe && subscriptionValid, quantity: this.qty });

    // free-shipping nudge — subscriptions always ship free; one-time counts toward the threshold
    const ship = this.querySelector('[data-pogi-ship]');
    if (ship && variant) {
      const msg = ship.querySelector('[data-pogi-ship-msg]');
      const fill = ship.querySelector('[data-pogi-ship-fill]');
      if (this.I?.js_shipping_supported === false) {
        ship.classList.remove('is-unlocked');
        if (msg) msg.innerHTML = this.t('js_ship_always_html', '<strong>See delivery options at checkout.</strong>');
        if (fill) fill.style.width = '0%';
      } else if (this.subscribe && subscriptionValid) {
        ship.classList.add('is-unlocked');
        if (msg) msg.innerHTML = this.t('js_ship_always_html', '<strong>Free shipping, always.</strong>');
        if (fill) fill.style.width = '100%';
      } else {
        const total = variant.price * this.qty;
        const remaining = Math.max(0, this.shipThreshold - total);
        if (remaining === 0) {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_unlocked_html', '<strong>Free shipping unlocked.</strong>');
        } else {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_remaining_html', "You're <strong>[[amount]]</strong> from free shipping.", { amount: this.money(remaining) });
        }
        if (fill) fill.style.width = '100%';
      }
    }
    // Roving tabindex LAST — after the pill display-sync and the [hidden] panel
    // toggles above, so a hidden/disabled radio is never left holding a group's
    // tab stop (Codex countersign #19, 2026-07-23).
    this.#rove();
    this.#recordV2Observation();
  }

  #ensureSellingPlanInput() {
    if (!this.nativeForm) return;
    if (!this.nativeForm.querySelector('input[name="selling_plan"]')) {
      const i = document.createElement('input'); i.type = 'hidden'; i.name = 'selling_plan'; this.nativeForm.appendChild(i);
    }
  }
  #changeQty(direction) {
    const variant = this.currentVariant();
    if (this.v2Mode === 'active' && this.v2Model && !variant) {
      this.#failV2(new Error('Cannot change quantity without one resolved Dispenser variant'));
      return;
    }
    if (this.v2Mode === 'active' && this.v2Model) {
      this.qty = this.v2Adapter.stepQuantity(variant.id, this.qty, direction).value;
    } else {
      this.qty = Math.max(1, this.qty + (direction < 0 ? -1 : 1));
    }
    this.#syncQty();
  }
  #syncQty() {
    const out = this.querySelector('[data-pogi-qty]');
    if (out) out.textContent = this.qty;
    if (this.v2Mode === 'active' && this.v2Adapter) {
      const variant = this.currentVariant();
      const selectedPlan = this.subscribe ? this.plan() : null;
      if (variant) {
        const state = this.v2Adapter.syncPurchaseInputs({
          variantId: variant.id,
          sellingPlanId: selectedPlan?.id ?? null,
          quantity: this.qty,
        });
        this.qty = state.value;
        if (out) out.textContent = this.qty;
      }
    } else {
      const nq = this.nativeForm && this.nativeForm.querySelector('input[name="quantity"]');
      if (nq) { nq.value = this.qty; nq.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    this.update();
  }
}

if (!customElements.get('pogi-buy-box-dispenser')) {
  customElements.define('pogi-buy-box-dispenser', PogiBuyBoxDispenser);
}
